/**
 * VNC Console Module
 *
 * Provides embedded VNC console using noVNC
 */

import type { VmVncInfo } from '../api/types.js';
import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { generateComboSelect } from '../ui/forms.js';
import { showRuntimePanel, runtimeHotplugDrive } from '../vm/runtime.js';
import { pasteDialog, formDialog } from '../ui/dialogs.js';
import RFBImport from '@novnc/novnc/lib/rfb.js';

// esbuild's CJS-to-ESM interop can double-wrap the default export when
// bundling as IIFE, leaving the constructor at .default.default instead of
// .default.  Unwrap at runtime so it works regardless.
const RFB: typeof RFBImport = typeof RFBImport === 'function'
  ? RFBImport
  : (RFBImport as unknown as { default: typeof RFBImport }).default;

let rfbConnection: InstanceType<typeof RFB> | null = null;
let currentVmId: number | null = null;
let currentVmHost: string | null = null;
let swapCtrlCapsLock: boolean = localStorage.getItem('vnc_swap_ctrl_caps') === 'true';

// Reconnection state
let currentWsUrl: string | null = null;
let currentPassword: string | undefined = undefined;
let currentViewOnly: boolean = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay: number = 1000; // Start at 1 second
const MAX_RECONNECT_DELAY = 30000; // Cap at 30 seconds

// X11 keysyms for key remapping
const XK_Control_L = 0xFFE3;
const XK_Control_R = 0xFFE4;
const XK_Caps_Lock = 0xFFE5;

// Extended RFB interface for sendKey method (not in @types/novnc)
interface RFBWithSendKey {
  sendKey(keysym: number, code: string | null, down?: boolean): void;
}

// Keyboard remapping: swap Ctrl and CapsLock
function setupKeyboardRemapping(container: HTMLElement): void {
  const handleKey = (e: KeyboardEvent) => {
    if (!rfbConnection || !swapCtrlCapsLock) return;

    // Only intercept keys we want to remap
    const isCapsLock = e.code === 'CapsLock';
    const isLeftCtrl = e.code === 'ControlLeft';
    const isRightCtrl = e.code === 'ControlRight';

    if (!isCapsLock && !isLeftCtrl && !isRightCtrl) return;

    // Prevent the original key from reaching noVNC
    e.preventDefault();
    e.stopPropagation();

    const down = e.type === 'keydown';
    const rfb = rfbConnection as unknown as RFBWithSendKey;

    if (isCapsLock) {
      // CapsLock -> send Left Ctrl
      rfb.sendKey(XK_Control_L, 'ControlLeft', down);
    } else if (isLeftCtrl) {
      // Left Ctrl -> send CapsLock
      rfb.sendKey(XK_Caps_Lock, 'CapsLock', down);
    } else if (isRightCtrl) {
      // Right Ctrl stays as Ctrl (only swap left Ctrl with CapsLock)
      rfb.sendKey(XK_Control_R, 'ControlRight', down);
    }
  };

  // Use capture phase to intercept before noVNC's handlers
  container.addEventListener('keydown', handleKey, { capture: true });
  container.addEventListener('keyup', handleKey, { capture: true });
}

/**
 * Toggle Ctrl/CapsLock swap and update button state
 */
export function toggleSwapCtrlCaps(): void {
  swapCtrlCapsLock = !swapCtrlCapsLock;
  localStorage.setItem('vnc_swap_ctrl_caps', swapCtrlCapsLock ? 'true' : 'false');
  updateSwapCtrlCapsButton();
}

function updateSwapCtrlCapsButton(): void {
  const btn = document.getElementById('vnc-swap-ctrl-caps-btn');
  if (btn) {
    btn.classList.toggle('vnc-btn-active', swapCtrlCapsLock);
    btn.title = swapCtrlCapsLock ? 'Ctrl/CapsLock Swap: ON' : 'Ctrl/CapsLock Swap: OFF';
  }
}

/**
 * Toggle the mobile on-screen keyboard.
 * Focuses/blurs noVNC's internal textarea to show/hide the soft keyboard.
 */
export function toggleKeyboard(): void {
  const screen = document.getElementById('vnc-screen');
  if (!screen || !rfbConnection) return;

  const textarea = screen.querySelector('textarea');
  if (!textarea) return;

  if (document.activeElement === textarea) {
    textarea.blur();
  } else {
    textarea.focus();
  }

  setTimeout(updateKeyboardButton, 0);
}

function updateKeyboardButton(): void {
  const screen = document.getElementById('vnc-screen');
  const textarea = screen?.querySelector('textarea');
  const active = textarea != null && document.activeElement === textarea;
  const btn = document.getElementById('vnc-keyboard-btn');
  if (btn) {
    btn.classList.toggle('vnc-btn-active', active);
  }
}

/**
 * Open a VNC console dialog for a VM
 *
 * @param vmId - VM ID
 * @param vmName - VM name for dialog title
 * @param vncInfo - VNC connection info
 * @param host - Target host where the VM runs (for delegated VMs)
 * @param wsPath - WebSocket path override (e.g. /ws/vm/:id/guest-vnc)
 * @param isGuest - If true, hide QEMU-specific toolbar buttons and adjust title
 */
export function openVncConsole(
  vmId: number, vmName: string, vncInfo: VmVncInfo,
  host?: string, wsPath?: string, isGuest?: boolean
): void {
  const dialogId = 'vnc-console-dialog';

  // Store VM ID and host for CD-ROM picker, hotplug, etc.
  currentVmId = vmId;
  currentVmHost = host ?? null;
  currentViewOnly = isGuest === true;

  // Remove existing dialog if any
  $(`#${dialogId}`).remove();

  // Always connect through the vmsmand WebSocket proxy
  const wsUrl = api.buildWsUrl(wsPath ?? `/ws/vm/${vmId}/vnc`);

  const swapBtnClass = swapCtrlCapsLock ? 'vnc-btn vnc-btn-active' : 'vnc-btn';
  const swapBtnTitle = swapCtrlCapsLock ? 'Ctrl/CapsLock Swap: ON' : 'Ctrl/CapsLock Swap: OFF';

  // Hide QEMU-specific buttons for guest VNC mode
  const qemuBtnStyle = isGuest ? ' style="display:none"' : '';
  // In guest mode, input buttons start hidden until "Allow Control" is toggled
  const inputInitStyle = isGuest ? ' style="display:none"' : '';

  const html = `
    <div id="${dialogId}">
      <div class="vnc-toolbar">
        ${isGuest ? `<button class="ui-button vnc-btn" id="vnc-allow-control-btn" onclick="vncToggleAllowControl()" title="Allow keyboard and mouse input">Allow Control</button>` : ''}
        <button class="ui-button vnc-btn vnc-input-btn" onclick="vncSendCtrlAltDel()" title="Send Ctrl+Alt+Del"${inputInitStyle}>
          Ctrl+Alt+Del
        </button>
        <button class="ui-button vnc-btn" onclick="vncToggleFullscreen()" title="Toggle Fullscreen">
          Fullscreen
        </button>
        <button class="ui-button vnc-btn" onclick="vncShowCdromPicker()" title="Mount/Unmount CD-ROM"${qemuBtnStyle}>
          CD
        </button>
        <button class="ui-button vnc-btn" onclick="vncHotplugDrive()" title="Hotplug a Drive"${qemuBtnStyle}>
          Drive
        </button>
        <button class="ui-button vnc-btn" onclick="vncShowDevices()" title="USB and Drive Hotplug"${qemuBtnStyle}>
          Devices
        </button>
        <button class="ui-button ${swapBtnClass} vnc-input-btn" id="vnc-swap-ctrl-caps-btn" onclick="vncToggleSwapCtrlCaps()" title="${swapBtnTitle}"${inputInitStyle}>
          Ctrl/Caps
        </button>
        <button class="ui-button vnc-btn vnc-input-btn" onclick="vncPaste()" title="Paste clipboard as keystrokes"${inputInitStyle}>
          Paste
        </button>
        <button class="ui-button vnc-btn mobile-kbd-btn vnc-input-btn" id="vnc-keyboard-btn" onclick="vncToggleKeyboard()" title="Toggle On-Screen Keyboard"${inputInitStyle}>
          Keyboard
        </button>
        <span class="vnc-status" id="vnc-status">Connecting...</span>
      </div>
      <div id="vnc-screen" class="vnc-screen"></div>
    </div>
  `;

  const titlePrefix = isGuest ? 'Screen View' : 'Console';

  $(html).appendTo('body').dialog({
    modal: true,
    title: `${titlePrefix} - ${vmName}`,
    width: Math.min(1280, $(window).width()! - 100),
    height: Math.min(800, $(window).height()! - 100),
    resizable: true,
    close: function () {
      disconnectVnc();
      $(this).remove();
    },
    open: function () {
      initVncConnection(wsUrl, vncInfo.password);
    },
  });
}

/**
 * Cancel any pending reconnection attempt
 */
function cancelReconnect(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

/**
 * Schedule a reconnection attempt with exponential backoff
 */
function scheduleReconnect(): void {
  if (!currentWsUrl) return;

  const statusEl = document.getElementById('vnc-status');
  if (statusEl) {
    const delaySec = Math.round(reconnectDelay / 1000);
    statusEl.textContent = `Reconnecting in ${delaySec}s...`;
    statusEl.style.color = '#c90';
  }

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    if (currentWsUrl) {
      const statusEl = document.getElementById('vnc-status');
      if (statusEl) {
        statusEl.textContent = 'Reconnecting...';
        statusEl.style.color = '#c90';
      }
      initVncConnection(currentWsUrl, currentPassword);
    }
  }, reconnectDelay);

  // Exponential backoff: double the delay for next attempt, cap at max
  reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
}

/**
 * Initialize VNC connection
 */
function initVncConnection(wsUrl: string, password?: string): void {
  const container = document.getElementById('vnc-screen');
  const statusEl = document.getElementById('vnc-status');

  if (!container || !statusEl) return;

  // Store connection params for reconnection
  currentWsUrl = wsUrl;
  currentPassword = password;

  // Clear any existing content in the container (from previous connection)
  // noVNC creates its own canvas/elements
  if (rfbConnection) {
    try {
      rfbConnection.disconnect();
    } catch {
      // Ignore errors during cleanup
    }
    rfbConnection = null;
  }

  try {
    // Create RFB connection
    const options: { credentials?: { password?: string } } = {};
    if (password) {
      options.credentials = { password };
    }

    rfbConnection = new RFB(container, wsUrl, options);
    rfbConnection.scaleViewport = true;
    rfbConnection.resizeSession = true;
    rfbConnection.viewOnly = currentViewOnly;

    // Set up Ctrl/CapsLock swap
    setupKeyboardRemapping(container);

    // When mouse enters the VNC screen, focus the canvas to capture keyboard input.
    // This prevents toolbar buttons from stealing Enter/Space key presses after being clicked.
    // Use a one-time setup flag to avoid adding duplicate listeners on reconnect.
    if (!(container as HTMLElement & { _vncMouseEnterSetup?: boolean })._vncMouseEnterSetup) {
      (container as HTMLElement & { _vncMouseEnterSetup?: boolean })._vncMouseEnterSetup = true;
      container.addEventListener('mouseenter', () => {
        // Blur any focused toolbar button
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        // Focus the canvas
        const canvas = container.querySelector('canvas');
        canvas?.focus();
      });
    }

    // Event handlers
    rfbConnection.addEventListener('connect', () => {
      // Reset backoff on successful connection
      reconnectDelay = 1000;
      cancelReconnect();

      statusEl.textContent = 'Connected';
      statusEl.style.color = '#0a0';
      // Focus the VNC canvas so keyboard input goes to the VM, not the toolbar buttons
      const canvas = container.querySelector('canvas');
      canvas?.focus();

      // Sync mobile keyboard button state when focus is lost externally
      const textarea = container.querySelector('textarea');
      textarea?.addEventListener('blur', updateKeyboardButton);
    });

    rfbConnection.addEventListener('disconnect', (e: CustomEvent) => {
      const detail = e.detail as { clean?: boolean };
      rfbConnection = null;

      if (detail.clean) {
        // Clean disconnect (user closed, or VM stopped) - don't reconnect
        statusEl.textContent = 'Disconnected';
        statusEl.style.color = '#666';
      } else {
        // Unclean disconnect - attempt reconnection
        statusEl.textContent = 'Connection lost';
        statusEl.style.color = '#c00';
        scheduleReconnect();
      }
    });

    rfbConnection.addEventListener('desktopname', (e: CustomEvent) => {
      const detail = e.detail as { name?: string };
      const dialog = $('#vnc-console-dialog').closest('.ui-dialog');
      if (dialog.length && detail.name) {
        dialog.find('.ui-dialog-title').text(`Console - ${detail.name}`);
      }
    });

    rfbConnection.addEventListener('credentialsrequired', () => {
      const rfb = rfbConnection;
      if (!rfb) return;

      statusEl.textContent = 'Password required';
      statusEl.style.color = '#c90';

      formDialog(
        'VNC Password',
        '<label for="vnc-cred-password">Password:</label>' +
        '<input type="password" id="vnc-cred-password" style="width:100%;margin-top:4px">',
        () => {
          const pw = ($('#vnc-cred-password').val() as string) || '';
          rfb.sendCredentials({ password: pw });
        },
        {
          submitLabel: 'Connect',
          width: 300,
          onOpen: () => { $('#vnc-cred-password').trigger('focus'); },
        }
      );
    });

  } catch (err) {
    console.error('Failed to create VNC connection:', err);
    statusEl.textContent = `Failed: ${err instanceof Error ? err.message : 'Unknown error'}`;
    statusEl.style.color = '#c00';
    // Schedule reconnect on connection failure too
    scheduleReconnect();
  }
}

/**
 * Disconnect VNC and stop any reconnection attempts
 */
export function disconnectVnc(): void {
  // Cancel any pending reconnection
  cancelReconnect();

  // Clear connection state to prevent reconnection
  currentWsUrl = null;
  currentPassword = undefined;
  reconnectDelay = 1000; // Reset backoff

  if (rfbConnection) {
    rfbConnection.disconnect();
    rfbConnection = null;
  }
}

/**
 * Send Ctrl+Alt+Del
 */
export function sendCtrlAltDel(): void {
  if (rfbConnection) {
    rfbConnection.sendCtrlAltDel();
  }
}

/**
 * Toggle view-only mode for guest VNC (Allow Control button).
 * Shows/hides input-related toolbar buttons and flips rfb.viewOnly.
 */
export function toggleAllowControl(): void {
  currentViewOnly = !currentViewOnly;
  if (rfbConnection) {
    rfbConnection.viewOnly = currentViewOnly;
  }
  const btn = document.getElementById('vnc-allow-control-btn');
  if (btn) {
    btn.classList.toggle('vnc-btn-active', !currentViewOnly);
    btn.title = currentViewOnly ? 'Allow keyboard and mouse input' : 'Input enabled — click to disable';
  }
  // Show/hide input-related buttons
  document.querySelectorAll('.vnc-input-btn').forEach(el => {
    (el as HTMLElement).style.display = currentViewOnly ? 'none' : '';
  });
}

/**
 * Paste clipboard text by sending individual key events via VNC.
 * Characters are sent with a small delay between them to avoid
 * overwhelming the guest's keyboard input buffer.
 */
const VNC_PASTE_DELAY_MS = 10;

// X11 keysym constants
const XK_Return    = 0xFF0D;
const XK_Tab       = 0xFF09;
const XK_BackSpace = 0xFF08;
const XK_Shift_L   = 0xFFE1;

// Map of shifted characters to their base keysym (US keyboard layout)
// When we encounter a shifted char, we send Shift down, base key, Shift up
const SHIFTED_CHAR_MAP: Record<string, number> = {
  // Uppercase letters -> lowercase keysyms
  ...((): Record<string, number> => {
    const map: Record<string, number> = {};
    for (let i = 65; i <= 90; i++) { // A-Z
      map[String.fromCharCode(i)] = i + 32; // a-z
    }
    return map;
  })(),
  // Number row symbols -> base number keysyms
  '!': 0x31, // 1
  '@': 0x32, // 2
  '#': 0x33, // 3
  '$': 0x34, // 4
  '%': 0x35, // 5
  '^': 0x36, // 6
  '&': 0x37, // 7
  '*': 0x38, // 8
  '(': 0x39, // 9
  ')': 0x30, // 0
  // Other shifted symbols
  '~': 0x60, // `
  '_': 0x2d, // -
  '+': 0x3d, // =
  '{': 0x5b, // [
  '}': 0x5d, // ]
  '|': 0x5c, // \
  ':': 0x3b, // ;
  '"': 0x27, // '
  '<': 0x2c, // ,
  '>': 0x2e, // .
  '?': 0x2f, // /
};

interface KeyAction {
  keysym: number;
  shifted: boolean;
}

function charToKeyAction(char: string): KeyAction | null {
  const cp = char.codePointAt(0);
  if (cp === undefined) return null;

  if (cp === 0x0A || cp === 0x0D) return { keysym: XK_Return, shifted: false };
  if (cp === 0x09) return { keysym: XK_Tab, shifted: false };
  if (cp === 0x08) return { keysym: XK_BackSpace, shifted: false };

  // Check if this is a shifted character
  if (char in SHIFTED_CHAR_MAP) {
    return { keysym: SHIFTED_CHAR_MAP[char], shifted: true };
  }

  // Latin-1 range (space through U+00FF): keysym = code point
  if (cp >= 0x20 && cp <= 0x00FF) return { keysym: cp, shifted: false };

  // Unicode above Latin-1: X11 Unicode keysym convention
  if (cp >= 0x0100) return { keysym: 0x01000000 | cp, shifted: false };

  return null;  // Unprintable control character
}

export function pasteClipboard(): void {
  if (!rfbConnection) return;

  pasteDialog((text) => {
    if (!rfbConnection) return;

    const rfb = rfbConnection as unknown as RFBWithSendKey;
    const chars = Array.from(text);
    let i = 0;

    function sendNext(): void {
      if (!rfbConnection || i >= chars.length) return;

      // Skip \r in \r\n pairs — the \n will send Return
      if (chars[i] === '\r' && i + 1 < chars.length && chars[i + 1] === '\n') {
        i++;
        sendNext();
        return;
      }

      const action = charToKeyAction(chars[i]);
      if (action !== null) {
        if (action.shifted) {
          // Send: Shift down, key press, Shift up
          rfb.sendKey(XK_Shift_L, 'ShiftLeft', true);
          rfb.sendKey(action.keysym, null);
          rfb.sendKey(XK_Shift_L, 'ShiftLeft', false);
        } else {
          rfb.sendKey(action.keysym, null);
        }
      }
      i++;
      if (i < chars.length) {
        setTimeout(sendNext, VNC_PASTE_DELAY_MS);
      }
    }

    sendNext();
  });
}

/**
 * Toggle fullscreen mode
 */
export function toggleFullscreen(): void {
  const screen = document.getElementById('vnc-screen');
  if (!screen) return;

  if (!document.fullscreenElement) {
    screen.requestFullscreen?.();
  } else {
    document.exitFullscreen?.();
  }
}

/**
 * Show CD-ROM ISO picker dialog
 */
export async function showCdromPicker(): Promise<void> {
  if (currentVmId === null) return;
  const vmId = currentVmId;

  const isoData = await apiCall(api.getIsoImages(currentVmHost ?? undefined), 'Failed to Load ISO Images');
  if (!isoData) return;

  const content = '<div><h4>Select ISO Image</h4>'
    + generateComboSelect('cdrom_iso', 'none', isoData)
    + '</div>';

  $('<div></div>')
    .appendTo('body')
    .html(content)
    .dialog({
      modal: true,
      title: 'Mount CD-ROM',
      zIndex: 10001,
      autoOpen: true,
      width: 'auto',
      resizable: false,
      buttons: {
        Mount: async function () {
          const iso = $('#cdrom_iso').val() as string;
          if (!iso || iso === 'none') return;
          const dlg = $(this);
          dlg.dialog('disable');
          const result = await apiCall(api.mountCdrom(vmId, iso), 'Failed to Mount CD-ROM');
          if (result !== null) {
            dlg.dialog('close');
          } else {
            dlg.dialog('enable');
          }
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Hotplug a drive to the current VM
 */
export function hotplugDrive(): void {
  if (currentVmId === null) return;
  runtimeHotplugDrive(currentVmId, currentVmHost ?? undefined);
}

/**
 * Open the USB/Drive hotplug panel for the current VM
 */
export function showDevices(): void {
  if (currentVmId === null) return;
  showRuntimePanel(currentVmId, currentVmHost ?? undefined);
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).vncSendCtrlAltDel = sendCtrlAltDel;
  (window as unknown as Record<string, unknown>).vncToggleAllowControl = toggleAllowControl;
  (window as unknown as Record<string, unknown>).vncToggleFullscreen = toggleFullscreen;
  (window as unknown as Record<string, unknown>).vncShowCdromPicker = showCdromPicker;
  (window as unknown as Record<string, unknown>).vncHotplugDrive = hotplugDrive;
  (window as unknown as Record<string, unknown>).vncShowDevices = showDevices;
  (window as unknown as Record<string, unknown>).vncToggleSwapCtrlCaps = toggleSwapCtrlCaps;
  (window as unknown as Record<string, unknown>).vncPaste = pasteClipboard;
  (window as unknown as Record<string, unknown>).vncToggleKeyboard = toggleKeyboard;
  (window as unknown as Record<string, unknown>).disconnectVnc = disconnectVnc;
}
